/**
 * Interactive guided tour — dimmed UI with a bright spotlight cutout.
 * Tip stays on-screen; target scrolls into view before each step.
 */

interface TourStep {
  target: string;
  title: string;
  body: string;
  screen?: string;
  placement?: "top" | "bottom" | "left" | "right" | "auto";
}

const STEPS: TourStep[] = [
  {
    target: ".brand-3d",
    title: "Welcome, Cadet!",
    body: "Ultimate Piano Tutor is your full classical course — lessons, exams, arcade drills, and a music vault.",
    placement: "bottom",
  },
  {
    target: '[data-portal="campaign"]',
    title: "Campaign",
    body: "Your main path. Clear classes stage-by-stage, then defeat Gatekeeper exams to rank up.",
    placement: "auto",
  },
  {
    target: '[data-portal="play"]',
    title: "Practice",
    body: "Free warm-up on the full keyboard. Use laptop keys or plug in a MIDI controller.",
    placement: "auto",
  },
  {
    target: '[data-portal="arcade"]',
    title: "Arcade",
    body: "Mini-games that sharpen pitch and rhythm — play between lessons for XP and fun.",
    placement: "auto",
  },
  {
    target: '[data-portal="vault"]',
    title: "Vault",
    body: "Library pieces + your own MIDI files. Demo (watch keys light up), Learn, or Performance.",
    placement: "auto",
  },
  {
    target: '[data-portal="gear"]',
    title: "Gear",
    body: "Audio, MIDI, keyboard size, and latency. Come here if something feels off.",
    placement: "auto",
  },
  {
    target: ".armory-strip",
    title: "Shop",
    body: "One purchase unlocks every stage, exams, and removes ads. Progress stays on your account.",
    placement: "top",
  },
  {
    target: ".profile-card",
    title: "Your Profile",
    body: "Sign in with Google, track rank & XP, and keep streaks alive across devices.",
    placement: "bottom",
  },
];

let overlay: HTMLDivElement | null = null;
let spotlight: HTMLDivElement | null = null;
let tooltip: HTMLDivElement | null = null;
let currentStep = 0;
let navigateFn: ((screen: string) => void) | null = null;
let positionRaf = 0;

function createOverlay(): void {
  overlay = document.createElement("div");
  overlay.className = "tour-overlay";
  overlay.innerHTML = `
    <div class="tour-spotlight" id="tour-spotlight" aria-hidden="true"></div>
    <div class="tour-tooltip" id="tour-tooltip" role="dialog" aria-modal="true">
      <p class="tour-step-count" id="tour-count"></p>
      <h3 class="tour-title" id="tour-title"></h3>
      <p class="tour-body" id="tour-body"></p>
      <div class="tour-actions">
        <button type="button" class="secondary" id="tour-skip">Skip</button>
        <button type="button" class="btn-gold" id="tour-next">Next</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  spotlight = overlay.querySelector("#tour-spotlight")!;
  tooltip = overlay.querySelector("#tour-tooltip")!;

  overlay.querySelector("#tour-skip")!.addEventListener("click", (e) => {
    e.stopPropagation();
    endTour();
  });
  overlay.querySelector("#tour-next")!.addEventListener("click", (e) => {
    e.stopPropagation();
    nextStep();
  });
  // Don't advance on overlay click — only Next / Skip (avoids accidental skips)
}

function pickPlacement(rect: DOMRect, preferred: TourStep["placement"]): "top" | "bottom" {
  if (preferred === "top" || preferred === "bottom") return preferred;
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  return spaceBelow >= 180 || spaceBelow >= spaceAbove ? "bottom" : "top";
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function positionAround(targetEl: Element, step: TourStep): void {
  if (!spotlight || !tooltip || !overlay) return;
  const rect = targetEl.getBoundingClientRect();
  const pad = 10;

  // Spotlight cutout (overlay itself is transparent; dimming comes from box-shadow)
  spotlight.style.top = `${rect.top - pad}px`;
  spotlight.style.left = `${rect.left - pad}px`;
  spotlight.style.width = `${Math.max(40, rect.width + pad * 2)}px`;
  spotlight.style.height = `${Math.max(40, rect.height + pad * 2)}px`;
  spotlight.classList.add("is-on");

  const ttW = Math.min(340, window.innerWidth - 24);
  tooltip.style.width = `${ttW}px`;
  tooltip.style.maxWidth = `${ttW}px`;

  // Measure tooltip after content is set
  const th = tooltip.offsetHeight || 160;
  const placement = pickPlacement(rect, step.placement ?? "auto");

  let top: number;
  if (placement === "bottom") {
    top = rect.bottom + pad + 14;
    if (top + th > window.innerHeight - 12) {
      top = Math.max(12, rect.top - th - pad - 14);
    }
  } else {
    top = rect.top - th - pad - 14;
    if (top < 12) {
      top = Math.min(window.innerHeight - th - 12, rect.bottom + pad + 14);
    }
  }

  const left = clamp(rect.left + rect.width / 2 - ttW / 2, 12, window.innerWidth - ttW - 12);
  tooltip.style.top = `${clamp(top, 12, window.innerHeight - th - 12)}px`;
  tooltip.style.left = `${left}px`;
  tooltip.style.bottom = "auto";
  tooltip.style.right = "auto";
  tooltip.classList.add("is-on");
}

function waitForLayout(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function showStep(): Promise<void> {
  if (!overlay || !tooltip) return;
  const step = STEPS[currentStep];
  if (!step) {
    endTour();
    return;
  }

  tooltip.classList.remove("is-on");
  spotlight?.classList.remove("is-on");

  if (step.screen && navigateFn) navigateFn(step.screen);
  if (navigateFn && !step.screen) navigateFn("home");

  await waitForLayout(80);

  const el = document.querySelector(step.target);
  if (!el) {
    currentStep++;
    void showStep();
    return;
  }

  // Scroll page + any scroll parents so the target is visible under the overlay
  el.scrollIntoView({ behavior: "instant" as ScrollBehavior, block: "center", inline: "nearest" });
  // Also scroll window in case nested scroll didn't move enough
  const r0 = el.getBoundingClientRect();
  if (r0.top < 80 || r0.bottom > window.innerHeight - 80) {
    window.scrollBy({ top: r0.top - window.innerHeight / 2, behavior: "instant" as ScrollBehavior });
  }

  await waitForLayout(120);

  tooltip.querySelector("#tour-count")!.textContent = `STEP ${currentStep + 1} / ${STEPS.length}`;
  tooltip.querySelector("#tour-title")!.textContent = step.title;
  tooltip.querySelector("#tour-body")!.textContent = step.body;
  const nextBtn = tooltip.querySelector<HTMLButtonElement>("#tour-next")!;
  nextBtn.textContent = currentStep === STEPS.length - 1 ? "Finish" : "Next";

  cancelAnimationFrame(positionRaf);
  positionRaf = requestAnimationFrame(() => {
    positionAround(el, step);
    // Re-measure after paint in case font metrics shifted height
    requestAnimationFrame(() => positionAround(el, step));
  });
}

function nextStep(): void {
  currentStep++;
  if (currentStep >= STEPS.length) {
    endTour();
    return;
  }
  void showStep();
}

function endTour(): void {
  cancelAnimationFrame(positionRaf);
  if (overlay) {
    overlay.remove();
    overlay = null;
  }
  spotlight = null;
  tooltip = null;
  currentStep = 0;
  try {
    localStorage.setItem("piano_tour_seen", "1");
  } catch {
    /* ignore */
  }
}

export function startTour(navigate: (screen: string) => void): void {
  if (overlay) return;
  navigateFn = navigate;
  currentStep = 0;
  createOverlay();
  navigate("home");
  void showStep();
}

export function shouldAutoTour(): boolean {
  try {
    return !localStorage.getItem("piano_tour_seen");
  } catch {
    return false;
  }
}
